import React, { useState, useEffect } from "react";
import { useRound } from "context/roundContext";
import { collection, query, limit, getDocs } from "firebase/firestore";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import { classNames } from "utils/utils";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useLeague } from "context/leagueContext";
import { SCORES_2ND_HALF } from "utils/constants";
const LmsUserPicks = () => {
  const {
    state: { currentRound, roundEvents },
  } = useRound();
  const {
    state: { leagueInfo },
  } = useLeague();

  const [userPicks, setUserPicks] = useState([]);

  const firestore = useFirestore();

  const roundUsersCollection = collection(
    firestore,
    "userTournaments/" + leagueInfo?.id + "/users"
  );

  let roundStarted =
    currentRound?.deadline?.seconds * 1000 < new Date() ||
    currentRound?.settled;

  const roundUsersQuery = query(roundUsersCollection);
  //const roundUsersQuery = query(roundUsersCollection, limit(30));

  const { status: roundUsersStatus, data: roundUsersList } =
    useFirestoreCollectionData(roundUsersQuery, {
      idField: "id",
    });

  useEffect(() => {
    if (roundUsersList) {
      fetchEvents(roundUsersList);
    }
  }, [roundUsersList]);

  const fetchEvents = async (list) => {
    const usersWithEvents = await Promise.all(
      list.map(async (user) => {
        const eventsCollection = collection(
          firestore,
          "userTournaments/" +
            leagueInfo?.id +
            "/rounds/" +
            currentRound.id +
            "/users/" +
            user.id +
            "/events"
        );
        const eventsQuery = query(eventsCollection, limit(1));
        const eventsSnapshot = await getDocs(eventsQuery);
        const eventDoc = eventsSnapshot.docs[0];
        let eventData = roundEvents.find((x) => x.id === eventDoc?.id);
        const event = eventDoc
          ? { id: eventDoc.id, ...eventDoc.data(), eventData: eventData }
          : null;
        return { ...user, event };
      })
    );

    setUserPicks(usersWithEvents);
  };

  const groupedByPick = userPicks
    .filter(function (item) {
      if (!currentRound?.settled) {
        if (!item.gameOver) {
          return item;
        } else return;
      } else if (
        item?.passedRounds?.some((el) => el == currentRound.id) ||
        item?.exitRound == currentRound.id
      ) {
        return item;
      } else return;
    })

    .reduce((entryMap, e) => {
      const key = e.event ? e.event : "No pick";
      const existingEntries = entryMap.get(key) || [];
      entryMap.set(key, [...existingEntries, e]);
      return entryMap;
    }, new Map());

  const participantList = [...groupedByPick]
    .sort(function (a, b) {
      return !a[0]?.teamId || !b[0]?.teamId
        ? -1
        : a[0].team.localeCompare(b[0].team);
    })

    .map(([key, value]) => {
      const { settled, result, scores, teams } = key?.eventData || {};

      const homeTeamScore = scores?.find(
        (x) =>
          x.type_id === SCORES_2ND_HALF && x.participant_id === teams?.home?.id
      )?.score?.goals;

      const awayTeamScore = scores?.find(
        (x) =>
          x.type_id === SCORES_2ND_HALF && x.participant_id === teams?.away?.id
      )?.score?.goals;

      return (
        <div
          className={classNames(
            !key?.teamId && "border-red-500 border-l-4",
            result && settled
              ? key?.pick === result
                ? "border-customGreen border-l-4"
                : result === 2
                ? " border-yellow-500 border-l-4"
                : " border-red-500 border-l-4"
              : "",
            "flex flex-col mb-4 bg-white rounded-md"
          )}
          key={key?.teamId}
        >
          <div className="flex py-2 border-b border-gray-300   ">
            {key?.teamId ? (
              <EventScore
                homeName={teams?.home?.name}
                awayName={teams?.away?.name}
                homeScore={homeTeamScore}
                awayScore={awayTeamScore}
                home={teams?.home?.id === key?.teamId}
              />
            ) : (
              <div className="flex text-xs w-full justify-center">
                <span>No pick</span>
              </div>
            )}
          </div>

          <div className="flex w-full justify-between rounded-md shadow-sm">
            <div className="flex flex-wrap pl-2 sm:pl-4 w-full ">
              {value.map((item) => {
                return (
                  <UserItem
                    item={item}
                    isExitRound={item.exitRound == currentRound?.id}
                  />
                );
              })}
            </div>
          </div>
        </div>
      );
    });
  return (
    <div className="flex flex-col mt-2 w-full">
      {roundUsersStatus === "loading" ? (
        <>Loading...</>
      ) : (
        <div className="flex flex-col ">{participantList}</div>
      )}
    </div>
  );
};

export default LmsUserPicks;

const EventScore = ({ homeName, awayName, homeScore, awayScore, home }) => {
  return (
    <div className="flex justify-center items-center w-full  px-2 text-xs">
      <div className="grow basis-0 justify-end flex">
        <div className=" w-fit px-2  grid items-center justify-end">
          <span
            className={classNames(
              home ? "font-semibold" : "font-light",
              "truncate whitespace-nowrap"
            )}
          >
            {homeName ? homeName : "-"}
          </span>
        </div>
      </div>

      <div className="flex flex-none  space-x-1">
        <span className="flex py-1 px-2 text-black items-center justify-center bg-gray-200 rounded-md">
          {homeScore !== undefined ? homeScore : "-"}
        </span>
        <span className="flex py-1 px-2 text-black items-center justify-center bg-gray-200 rounded-md">
          {awayScore !== undefined ? awayScore : "-"}
        </span>
      </div>

      <div className="grow basis-0 flex">
        <div className=" w-fit px-2  grid items-center justify-end">
          <span
            className={classNames(
              !home ? "font-semibold" : "font-light",
              "truncate whitespace-nowrap"
            )}
          >
            {awayName ? awayName : "-"}
          </span>
        </div>
      </div>
    </div>
  );
};

const UserItem = ({ item, isExitRound }) => {
  return (
    <div
      className="flex flex-col justify-center items-center mr-2 py-2 w-12 shrink-0"
      key={item.id}
    >
      <div className="grid grid-cols-1 justify-items-end items-end">
        <img
          src={item.photo}
          className={classNames(
            isExitRound
              ? item?.event?.eventData?.result !== 2
                ? "grayscale opacity-50"
                : "animate-grayout"
              : "",
            "rounded-full row-start-1 col-start-1 w-8 h-8"
          )}
          alt=""
        />
        {isExitRound ? (
          item?.event?.eventData?.result == 2 ? (
            <div className="flex -mb-1  -mr-2 row-start-1 col-start-1 leading-none z-10">
              <div className=" animate-right bg-yellow-500 w-3 h-4 text-xs justify-center items-center  flex text-white rounded-sm shadow-sm  mr-0.5"></div>

              <div className=" animate-appear flex opacity-0">
                <div className="animate-left delay-1000 bg-yellow-500 w-3 h-4 text-xs justify-center items-center  flex text-white rounded-sm shadow-sm  ">
                  <div className="animate-appear2 flex opacity-0">
                    <XMarkIcon className="h-3 w-3 text-white " />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-500 w-3 h-4 text-xs justify-center items-center  flex text-white rounded-sm shadow-sm  -mb-1 -mr-0.5 row-start-1 col-start-1 leading-none z-10">
              <XMarkIcon className="h-3 w-4 text-white " />
            </div>
          )
        ) : item?.event?.eventData?.result == 2 ? (
          <div className="bg-yellow-500 w-3 h-4 text-xs justify-center items-center  flex text-white rounded-sm shadow-sm  -mb-1 -mr-0.5 row-start-1 col-start-1 leading-none z-10"></div>
        ) : (
          ""
        )}
      </div>

      <div className="text-xs text-center pt-1 w-full truncate text-black">
        {item.nickname ? item.nickname : "-"}
      </div>
    </div>
  );
};
